<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import type { GeneratedRecordInspectionType } from '@/entities/GeneratedRecord';
  import { ColoredButton, FormInput, IconImage } from '@/shared/ui';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import { INSPECTION_IDENTIFIER_MAX_LENGTH, INSPECTION_IDENTIFIER_MODAL_TEXTS } from '../../config';
  import { useInspectionIdentifier } from '../../model/useInspectionIdentifier';

  const props = defineProps<{
    matrixId: number;
    inspectionType: GeneratedRecordInspectionType;
  }>();

  const emit = defineEmits<{
    close: [];
    identified: [identifier: string];
  }>();

  const texts = computed(() => INSPECTION_IDENTIFIER_MODAL_TEXTS[props.inspectionType]);

  const {
    identifier,
    requiredError,
    error,
    warnings,
    querying,
    submitLabel,
    setIdentifier,
    submit,
    discard,
  } = useInspectionIdentifier(
    () => props.matrixId,
    (accepted) => emit('identified', accepted)
  );

  const close = () => {
    discard();
    emit('close');
  };

  const INPUT_ID = 'inspection-identifier';
</script>

<template>
  <VueFinalModal
    :model-value="true"
    class="flex items-center justify-center"
    content-class="w-[95vw] max-w-[400px] rounded-2xl border border-gray-02 bg-white-01"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="
      (value: boolean) => {
        if (!value) close();
      }
    "
  >
    <div
      class="flex flex-col"
      data-testid="inspection-identifier-modal"
    >
      <div class="flex h-12 justify-end p-3">
        <button
          type="button"
          class="text-gray-03"
          aria-label="Fechar"
          data-testid="inspection-identifier-close"
          @click="close"
        >
          <IconImage
            :src="CloseIcon"
            alt=""
          />
        </button>
      </div>

      <form
        class="flex flex-col gap-8 px-6 pb-12"
        novalidate
        @submit.prevent="submit"
      >
        <h2
          class="text-center text-sm font-semibold text-gray-07"
          data-testid="inspection-identifier-title"
        >
          {{ texts.title }}
        </h2>

        <div class="flex flex-col gap-1.5">
          <FormInput
            :id="INPUT_ID"
            type="text"
            :label="texts.label"
            label-class="text-sm text-gray-07"
            :placeholder="texts.placeholder"
            :max-length="INSPECTION_IDENTIFIER_MAX_LENGTH"
            input-class="!h-11 !rounded-lg !py-0 bg-white-01 text-sm text-gray-07 shadow-xs placeholder:font-light placeholder:text-gray-03"
            :model-value="identifier"
            :error="requiredError || error || undefined"
            :disabled="querying"
            @update:model-value="setIdentifier"
          />
          <!-- FormInput draws its error without a live region, so screen readers hear it only from here. -->
          <p
            class="sr-only"
            role="alert"
            data-testid="inspection-identifier-error-announcement"
          >
            {{ requiredError || error || '' }}
          </p>
          <p
            v-for="warning in warnings"
            :key="warning.code"
            class="text-sm/5 text-badge-orange"
            role="status"
            :data-testid="`inspection-identifier-warning-${warning.code}`"
          >
            {{ warning.message }}
          </p>
        </div>

        <ColoredButton
          type="submit"
          class="w-full justify-center !text-sm"
          :text="submitLabel"
          :disabled="querying"
          :loading="querying"
          data-testid="inspection-identifier-submit"
        />
      </form>
    </div>
  </VueFinalModal>
</template>
